import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { Dashboard } from './pages/Dashboard';
import { SynopticDevices } from './pages/SynopticDevices';
import { Areas } from './pages/Areas';
import { Maps } from './pages/Maps';
import { Settings } from './pages/Settings';
import { Alarms } from './pages/Alarms';
import { Beacons } from './pages/Beacons';
import { useIsAuthenticated } from '@azure/msal-react';

import { IntlProvider, LocalizationProvider, load, loadMessages } from '@progress/kendo-react-intl';

import likelySubtags from 'cldr-core/supplemental/likelySubtags.json';
import currencyData from 'cldr-core/supplemental/currencyData.json';
import weekData from 'cldr-core/supplemental/weekData.json';
import dateFields from 'cldr-dates-full/main/en/dateFields.json';

import itNumbers from 'cldr-numbers-full/main/it/numbers.json';
import itLocalCurrency from 'cldr-numbers-full/main/it/currencies.json';
import itCaGregorian from 'cldr-dates-full/main/it/ca-gregorian.json';
import itDateFields from 'cldr-dates-full/main/it/dateFields.json';
import timeZoneNames from "cldr-dates-full/main/it/timeZoneNames.json";

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import translation_it from "./locales/it/translation_it.json"
import translation_en from "./locales/en/translation_en.json"
import { enMessages } from "./locales/en/en-US.js"
import { itMessages } from "./locales/it/it-IT.js"

import 'hammerjs';
import { AppContext } from './AppContext';
import { ConfigDevicesManagement } from './components/configuration/devices/ConfigDevicesManagement';
import { DrawerContainer } from './components/DrawerContainer';
import { ConfigBeacons } from './components/configuration/ConfigBeacons';
import { ConfigRulesTimeslot } from './components/configuration/rules/ConfigRulesTimeslot';
import { ConfigPrivacy } from './components/configuration/ConfigPrivacy';
import { ConfigDevicesBinding } from './components/configuration/devices/ConfigDevicesBinding';
import { ConfigLocatorCollector } from './components/configuration/locator/ConfigLocatorCollector';
import { ConfigLocatorServer } from './components/configuration/locator/ConfigLocatorServer';
import { ConfigDevicesEnable } from './components/configuration/devices/ConfigDevicesEnable';
import { ConfigRulesSubscription } from './components/configuration/rules/ConfigRulesSubscription';
import { ConfigRulesSubscriptionAPI } from './components/configuration/rules/ConfigRulesSubscriptionAPI';
import { ConfigRulesAlarmActionHandlers } from './components/configuration/rules/ConfigRulesAlarmActionHandlers';
import { RegistryGroups } from './components/configuration/registry/RegistryGroups';
import { RegistryJobs } from './components/configuration/registry/RegistryJobs';
import { HeatMap } from './components/configuration/analysis/HeatMap';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import axios from 'axios';
import { AccessToken } from './config/AccessToken';
import { NotificationGroup, Notification } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { loadingPanel } from './components/LoadingPanel';
import { setupAxiosInterceptors } from './config/setupAxiosInterceptors';
import { clearServerSettingsCache, fetchServerSettings } from './config/serverSettings';
import { ConfigZones } from './components/configuration/maps/ConfigZones';
import { ConfigMaps } from './components/configuration/maps/ConfigMaps';
import { SpaghettiChart } from './components/configuration/analysis/SpaghettiChart';
import { RegistryAccounts } from './components/configuration/registry/RegistryAccounts';
import { NewPassword } from './pages/NewPassword';
import { Report } from './components/configuration/analysis/Report';
import { getSharedMqttClient, isMqttConnectionEnabled, parseMqttMessage, setRuntimeMqttBrokerUrl, setRuntimeMqttCredentials } from './config/mqttClient';

import { alarm } from './assets/alarm'
import { SvgIcon } from '@progress/kendo-react-common';


load(
    likelySubtags,
    currencyData,
    weekData,
    dateFields,
    itNumbers,
    itLocalCurrency,
    itCaGregorian,
    itDateFields,
    timeZoneNames,
    itMessages
)

loadMessages(translation_it, "it-IT");
loadMessages(itMessages, 'it-IT');
loadMessages(translation_en, "en-US");
loadMessages(enMessages, 'en-US');


const App = () => {

    const navigate = useNavigate()

    const [theme, setTheme] = useState("https://unpkg.com/@progress/kendo-theme-bootstrap@latest/dist/all.css");

    //const localizationService = useLocalization();
    let languageId = localStorage.getItem('languageId');
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const [instance, accessTokenRequest] = AccessToken();

    const [loading, setLoading] = useState<any>(false);
    const [errorMessage, setErrorMessage] = useState<any>('');
    const [toggleShowErrorMessage, setToggleShowErrorMessage] = useState<boolean>(false);
    const [mqttNotifications, setMqttNotifications] = useState<Array<{ id: number; message: ReactNode; style?: 'info' | 'warning' | 'error' | 'success' }>>([]);
    const mqttNotificationIdRef = useRef(0);
    const hasFetchedGroupsInfoRef = useRef(false);
    const [runtimeBrokerUrl, setRuntimeBrokerUrlState] = useState<string>('');
    const [mqttClientRetryKey, setMqttClientRetryKey] = useState(0);
    const serverSettingsCacheRef = useRef<any[] | null>(null);
    const inFlightServerSettingsRequestRef = useRef<Promise<any[] | null> | null>(null);

    const emailNotFoundMessage: any = languageId == 'it-IT' ? 'Email non trovata' : 'Email not found';

    const changeTheme = (theme: string) => {
        setTheme(theme);
    }

    let authConfig: any;

    if (process.env.NODE_ENV === "production") {
        authConfig = require("./authConfig.production");
    }
    else { // Default to development
        authConfig = require("./authConfig.local");
    }

    //const fetchCompanyInfo = (accessToken:any) => {
    //    axios.get("https://graph.microsoft.com/v1.0/me", {
    //      headers: {
    //        Authorization: `Bearer ${accessToken}`,
    //      },
    //    })
    //      .then((res:any) => res.json())
    //      .then((data:any) => {
    //        console.log("User Info:", data);
    //        // You can also access the organization info here
    //        axios.get("https://graph.microsoft.com/v1.0/organization", {
    //          headers: {
    //            Authorization: `Bearer ${accessToken}`,
    //          },
    //        })
    //        .then((orgRes:any) => orgRes.json())
    //        .then((orgData:any) => {
    //            console.log("Organization Info:", orgData);
    //            alert(`Company Name: ${orgData.value[0].displayName}`);
    //        });
    //    }
    //)}



    const handleLogin = async () => {

        setLoading(true)

        try {
            const response: any = await instance.loginPopup(authConfig.loginRequest);
          
            const userEmail: string = response.account.username
            const username: string = response.account.name

            //  fetchCompanyInfo(response.accessToken)

            if (userEmail == null && username == '') {
                setErrorMessage(emailNotFoundMessage)
                if (!toggleShowErrorMessage) {
                    setTimeout(() => {
                        setToggleShowErrorMessage(false)
                    }, 10000)
                }
            }

            if (response) {

                const commonOperations = async (accessToken: any) => {
                    if (accessToken) {
                        localStorage.setItem('accessToken', accessToken);
                    }
                    const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
                    axios.get(`${SERVER_URL}/account/by-email/${userEmail}`, { headers })
                        .then((response) => {
                            let usernameType: any = response.data.data.userType;  
                            let email: any = response.data.data.email;  
                            let accountId: any = response.data.data.id;

                            localStorage.setItem('accountId', accountId)


                            switch (usernameType) {
                                case 'UbiquicomAdmin':
                                    localStorage.setItem('userType', usernameType)
                                    localStorage.setItem('user', username)
                                    localStorage.setItem('email', email)
                                    if (username == 'azure') {
                                        navigate('/home/azure_map')
                                    } else {
                                        navigate('/home/dashboard')
                                    }
                                    break;
                                case 'UbiquicomSuperAdmin':
                                    localStorage.setItem('userType', usernameType)
                                    localStorage.setItem('user', username)
                                    localStorage.setItem('email', email)
                                    navigate('/home/dashboard')
                                    break;
                                case 'GuestLocator':
                                    localStorage.setItem('userType', usernameType)
                                    localStorage.setItem('user', username)
                                    localStorage.setItem('email', email)
                                    navigate('/home/dashboard')
                                    break;
                                case 'GuestSynchro':
                                    localStorage.setItem('userType', usernameType)
                                    localStorage.setItem('user', username)
                                    localStorage.setItem('email', email)
                                    navigate('/home/synoptic')
                                    break;
                                default:
                                    localStorage.setItem('userType', usernameType)
                                    localStorage.setItem('user', username)
                                    localStorage.setItem('email', email)
                                    navigate('/home/dashboard')
                                    break;
                            }

                            setLoading(false)
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                instance.acquireTokenRedirect(accessTokenRequest);
                            }
                            setToggleShowErrorMessage(true)
                            
                            setErrorMessage(error?.response?.data?.errorMessage ?? error?.message)
                            if (!toggleShowErrorMessage) {
                                setTimeout(() => {
                                    setToggleShowErrorMessage(false)
                                }, 10000)
                            }
                            setLoading(false)
                        });
                }
                if (localStorage.getItem('accessToken') != null || localStorage.getItem('accessToken') != null) {
                    let accessToken = localStorage.getItem('accessToken') ?? localStorage.getItem('accessToken');
                    commonOperations(accessToken);
                } else {
                    await instance.initialize();
                    await instance.setActiveAccount(response.account);
                    await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                        let accessToken = accessTokenResponse.accessToken;
                        if (accessToken) {
                            localStorage.setItem('accessToken', accessToken);
                        }
                        commonOperations(accessToken);
                    })
                        .catch((error: any) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                instance.acquireTokenRedirect(accessTokenRequest);
                            }
                        });
                }

            }
        } catch (error) {
            setLoading(false)
        }
    }

    const [contextState, setContextState] = useState({
        localeId: localStorage.getItem('languageId') ?? 'it-IT',
        locale: localStorage.getItem('languageId') == 'en-US' ? 'English' : 'Italiano',
        languageId: localStorage.getItem('languageId') ?? 'it-IT'
    });

    const onLanguageChange: any = useCallback((event: any) => {
        localStorage.setItem('languageId', event.value.localeId)
        setContextState({ ...contextState, localeId: event.value.localeId })
    }, []
    );

    const [showPopupInfo, setShowPopupInfo] = useState<boolean>(false);
    const [showPopupAlarm, setShowPopupAlarm] = useState<boolean>(false);

    const handlePopupInfo = () => {
        setShowPopupInfo(!showPopupInfo)
    };

    const handlePopupAlarm = () => {
        setShowPopupAlarm(!showPopupAlarm)
    };

    const [batteryPercentage, setBatteryPercentage] = useState<any>(localStorage.getItem('batteryPercentage') ?? 10);
    const currBatteryPercentage = localStorage.getItem('batteryPercentage');
    if (currBatteryPercentage == null) {
        localStorage.setItem('batteryPercentage', '30')
    }

    const updateBatteryPercentage = (percentage: any) => {
        setBatteryPercentage(percentage)
    }

    const [valueRefreshAPI, setValueRefreshAPI] = useState<any>(localStorage.getItem('valueRefreshAPI') ?? 30);
    const currValueRefreshAPI = localStorage.getItem('refreshAPI');
    if (currValueRefreshAPI == null) {
        localStorage.setItem('refreshAPI', '30')
    }
    const updateValueRefreshAPI = (value: any) => {
        setValueRefreshAPI(value)
    }

    const [regularTag, setRegularTag] = useState<any>(localStorage.getItem('regularTag') ?? 5);
    const currValueRegularTag = localStorage.getItem('regularTag');
    if (currValueRegularTag == null) {
        localStorage.setItem('regularTag', '5')
    }
    const updateRegularTag = (value: any) => {
        setRegularTag(value)
    }

    const [statusDevice, setStatusDevice] = useState<any>(localStorage.getItem('statusDevice') ?? 75);
    const currValueStatusDevice = localStorage.getItem('statusDevice');
    if (currValueStatusDevice == null) {
        localStorage.setItem('statusDevice', '75')
    }
    const updateStatusDevice = (value: any) => {
        setStatusDevice(value)
    }

    let isAuthenticated: boolean = useIsAuthenticated();

    if (localStorage.getItem('accessToken') != null || localStorage.getItem('accessToken') != null) {
        isAuthenticated = true
    }

    const loadServerSettings = useCallback(async (): Promise<any[] | null> => {
        if (!isAuthenticated || !SERVER_URL) {
            return null;
        }

        if (serverSettingsCacheRef.current) {
            return serverSettingsCacheRef.current;
        }

        if (inFlightServerSettingsRequestRef.current) {
            return inFlightServerSettingsRequestRef.current;
        }

        const request = (async () => {
            const fetchWithToken = async (accessToken: any) => {
                const currentLanguageId = localStorage.getItem('languageId');
                return fetchServerSettings(SERVER_URL, accessToken, currentLanguageId);
            };

            const acquireFreshAccessToken = async (): Promise<string | null> => {
                await instance.initialize();

                const account = accessTokenRequest?.account
                    ?? instance.getActiveAccount()
                    ?? instance.getAllAccounts()?.[0];

                if (!account) {
                    return null;
                }

                const currentActiveAccount = instance.getActiveAccount();
                if (!currentActiveAccount || currentActiveAccount.homeAccountId !== account.homeAccountId) {
                    instance.setActiveAccount(account);
                }

                const tokenRequest: any = {
                    ...accessTokenRequest,
                    account,
                };

                const accessTokenResponse: any = await instance.acquireTokenSilent(tokenRequest);
                const nextAccessToken = accessTokenResponse?.accessToken ?? null;

                if (nextAccessToken) {
                    localStorage.setItem('accessToken', nextAccessToken);
                }

                return nextAccessToken;
            };

            try {
                const storedAccessToken = localStorage.getItem('accessToken');
                let settings = storedAccessToken != null
                    ? await fetchWithToken(storedAccessToken)
                    : null;

                if (!settings) {
                    const refreshedAccessToken = await acquireFreshAccessToken();
                    if (refreshedAccessToken) {
                        settings = await fetchWithToken(refreshedAccessToken);
                    }
                }

                serverSettingsCacheRef.current = settings;
                return settings;
            } catch (error: any) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }

                return null;
            }
        })();

        inFlightServerSettingsRequestRef.current = request;
        request.finally(() => {
            inFlightServerSettingsRequestRef.current = null;
        });

        return request;
    }, [isAuthenticated, SERVER_URL, instance, accessTokenRequest]);

    useEffect(() => {
        localStorage.setItem('languageId', contextState.localeId)
    }, [])

    const location = useLocation();

    const pushMqttNotification = useCallback((message: ReactNode) => {
        const nextId = ++mqttNotificationIdRef.current;
        setMqttNotifications((prev) => [...prev, { id: nextId, message }]);

        setTimeout(() => {
            setMqttNotifications((prev) => prev.filter((item) => item.id !== nextId));
        }, 5000);
    }, []);

    useEffect(() => {
        if (location.pathname != "/home/map") {
            localStorage.removeItem('deviceMac')
            localStorage.removeItem('deviceMacFromSynoptic')
            localStorage.removeItem('onlyBoundMap')
            localStorage.removeItem('deviceMapId')
            localStorage.removeItem('alarmInfoSelected')
            localStorage.removeItem('mapSelected')
            localStorage.removeItem('beaconId')
            localStorage.removeItem('beaconFromGrid')
            localStorage.removeItem('beaconsByMapId')
            localStorage.removeItem('mapIdByBeacon')
            localStorage.removeItem('liveStatus')
            localStorage.removeItem('onlyBound')
        }
    }, [location]); // Re-run effect when the 'location' object changes

    useEffect(() => {
        const cleanupInterceptors = setupAxiosInterceptors(navigate);
        return cleanupInterceptors;
    }, [navigate]);

    useEffect(() => {
        if (!isAuthenticated) {
            hasFetchedGroupsInfoRef.current = false;
            return;
        }

        if (hasFetchedGroupsInfoRef.current) {
            return;
        }

        hasFetchedGroupsInfoRef.current = true;

        const fetchGroupsInfo = async () => {
            const commonOperations = async (accessToken: any) => {
                const currentLanguageId = localStorage.getItem('languageId');
                const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': currentLanguageId };

                try {
                    const groupsResponse = await axios.get(`${SERVER_URL}/group/list`, { headers });
                    localStorage.setItem('groupsInfo', JSON.stringify(groupsResponse.data.data));
                } catch {
                    hasFetchedGroupsInfoRef.current = false;
                    localStorage.removeItem('groupsInfo');
                }
            };

            if (localStorage.getItem('accessToken') != null) {
                const accessToken = localStorage.getItem('accessToken');
                await commonOperations(accessToken);
            } else {
                await instance.initialize();
                const activeAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
                if (!activeAccount) {
                    hasFetchedGroupsInfoRef.current = false;
                    localStorage.removeItem('groupsInfo');
                    return;
                }

                await instance.setActiveAccount(activeAccount);

                const groupsAccessTokenRequest: any = {
                    scopes: [process.env.REACT_APP_SCOPE],
                    account: activeAccount,
                };

                await instance.acquireTokenSilent(groupsAccessTokenRequest)
                    .then((accessTokenResponse: any) => {
                        const accessToken = accessTokenResponse.accessToken;
                        return commonOperations(accessToken);
                    })
                    .catch(() => {
                        hasFetchedGroupsInfoRef.current = false;
                        localStorage.removeItem('groupsInfo');
                    });
            }
        };

        fetchGroupsInfo();
    }, [isAuthenticated, SERVER_URL, instance]);

    useEffect(() => {
        if (!isAuthenticated) {
            serverSettingsCacheRef.current = null;
            inFlightServerSettingsRequestRef.current = null;
            clearServerSettingsCache();
            return;
        }

        let isDisposed = false;

        const applySettings = (settings: any[]) => {
            const brokerUrlSetting = settings?.find((setting: any) => setting.id == 'MqttWebSocket_Url')?.value;
            const mqttUsernameSetting = settings?.find((setting: any) => setting.id == 'MqttUsername')?.value;
            const mqttPasswordSetting = settings?.find((setting: any) => setting.id == 'MqttPassword')?.value;
            const resolvedBrokerUrl = typeof brokerUrlSetting === 'string' ? brokerUrlSetting.trim() : '';
            const mqttUsername = typeof mqttUsernameSetting === 'string' ? mqttUsernameSetting.trim() : '';
            const mqttPassword = typeof mqttPasswordSetting === 'string' ? mqttPasswordSetting : '';

            if (isDisposed) {
                return;
            }

            setRuntimeBrokerUrlState(resolvedBrokerUrl);
            setRuntimeMqttCredentials(mqttUsername, mqttPassword);
            setRuntimeMqttBrokerUrl(resolvedBrokerUrl);
        };

        const resolveBrokerUrlFromSettings = async () => {
            const settings = await loadServerSettings();
            if (!settings) {
                if (!isDisposed) {
                    setRuntimeBrokerUrlState('');
                }
                return;
            }

            applySettings(settings);
        };

        resolveBrokerUrlFromSettings();

        return () => {
            isDisposed = true;
        };
    }, [isAuthenticated, loadServerSettings]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        if (!isMqttConnectionEnabled()) {
            return;
        }

        const client = getSharedMqttClient(runtimeBrokerUrl || SERVER_URL || undefined);
        if (!client) {
            const retryTimer = window.setTimeout(() => {
                setMqttClientRetryKey((prev) => prev + 1);
            }, 1500);

            return () => {
                window.clearTimeout(retryTimer);
            };
        }

        let isDisposed = false;
        let alarmTopic = '';

        const applyAlarmTopic = (nextTopicRaw: any) => {
            const nextTopic = typeof nextTopicRaw === 'string' ? nextTopicRaw.trim() : '';
            if (nextTopic === alarmTopic) {
                return;
            }

            const previousTopic = alarmTopic;
            alarmTopic = nextTopic;

            if (previousTopic) {
                client.unsubscribe(previousTopic);
            }

            if (alarmTopic && client.connected) {
                client.subscribe(alarmTopic);
            }
        };

        const resolveAlarmTopicFromSettings = async () => {
            const applySettings = (settings: any[]) => {
                const alarmTopicSetting = settings?.find((setting: any) => setting.id == 'MqttWebSocket_AlarmTopic')?.value;
                applyAlarmTopic(alarmTopicSetting);
            };

            const settings = await loadServerSettings();
            if (!settings) {
                applyAlarmTopic('');
                return;
            }

            applySettings(settings);
        };

        const onAlarmTopicChanged = (event: Event) => {
            const customEvent = event as CustomEvent<{ topic?: string }>;
            applyAlarmTopic(customEvent?.detail?.topic ?? '');
        };

        const subscribeToAlarmTopic = async () => {
            if (!alarmTopic) {
                await resolveAlarmTopicFromSettings();
            }

            if (isDisposed || !alarmTopic) {
                return;
            }

            client.subscribe(alarmTopic);
        };

        const onMessage = (topic: string, message: any) => {
            const rawMessage = message.toString();
            const parsedText = JSON.parse(parseMqttMessage(rawMessage));

            if (alarmTopic && topic === alarmTopic) {
                const groupsInfoRaw = localStorage.getItem('groupsInfo');
                let groupsInfo: any[] = [];

                if (groupsInfoRaw) {
                    try {
                        groupsInfo = JSON.parse(groupsInfoRaw);
                    } catch {
                        groupsInfo = [];
                    }
                }

                const eventGroupId = parsedText?.Event?.GroupId;
                const currentGroup = groupsInfo.find((group: any) => Number(group?.id) === Number(eventGroupId));

                const currentLanguageId = localStorage.getItem('languageId') ?? contextState.localeId;
                const alarmTitle = currentLanguageId === 'it-IT' ? 'Nuovo allarme' : 'New Alarm';
                const eventTypeName = parsedText?.Event?.EventType?.Name ?? '';
                const dateTime = parsedText?.Event?.Datetime ? new Date(parsedText.Event.Datetime).toLocaleString() : '';
                const groupColor = currentGroup?.color ?? '';
                const deviceMac = parsedText?.Event?.DeviceMac ?? '';
                const eventName = parsedText?.Event?.Name ?? '';
                const groupName = currentGroup?.name ?? '';
                const hasGroupColor = Boolean(groupColor);

                const alarmMessage = (
                    <div
                        style={{
                            lineHeight: 1.2,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 10,
                                marginBottom: 8,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(220, 53, 69, 0.14)',
                                        color: '#b42318',
                                    }}
                                >
                                    <SvgIcon icon={alarm} />
                                </span>
                                <span style={{ fontSize: 14, fontWeight: 700 }}>{alarmTitle}</span>
                            </div>
                            {groupName && (
                                <span
                                    style={{
                                        fontSize: 11,
                                        borderRadius: 999,
                                        border: '1px solid rgba(0, 0, 0, 0.15)',
                                        padding: '2px 8px',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {groupName}
                                </span>
                            )}
                        </div>

                        <div style={{ fontSize: 14, fontWeight: 600 }}>{eventName || '-'}</div>
                        <div style={{ fontSize: 12, opacity: 0.85 }}>{eventTypeName || '-'}</div>
                        <div style={{ fontSize: 12, opacity: 0.8 }}>{dateTime || '-'}</div>

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginTop: 8,
                                paddingTop: 8,
                                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <span
                                style={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: groupColor || '#d1d5db',
                                    border: hasGroupColor ? '1px solid rgba(0, 0, 0, 0.2)' : '1px dashed #6b7280',
                                    flex: '0 0 auto',
                                }}
                            />
                            <span style={{ fontFamily: 'monospace', fontSize: 12, letterSpacing: 0.2 }}>
                                {deviceMac || '-'}
                            </span>
                        </div>
                    </div>
                );
                pushMqttNotification(alarmMessage);
                return;
            }
        };

        client.on('message', onMessage);
        window.addEventListener('settings:mqtt-alarm-topic-changed', onAlarmTopicChanged as EventListener);

        Promise.resolve().finally(() => {
            if (isDisposed) {
                return;
            }

            client.on('connect', subscribeToAlarmTopic);

            if (client.connected) {
                subscribeToAlarmTopic();
            }
        });

        return () => {
            isDisposed = true;
            if (alarmTopic) {
                client.unsubscribe(alarmTopic);
            }
            client.off('connect', subscribeToAlarmTopic);
            client.off('message', onMessage);
            window.removeEventListener('settings:mqtt-alarm-topic-changed', onAlarmTopicChanged as EventListener);
        };
    }, [isAuthenticated, pushMqttNotification, runtimeBrokerUrl, loadServerSettings, SERVER_URL, mqttClientRetryKey]);

    const [searchParams] = useSearchParams();
    const token = searchParams.get("t");

    return (
        <div style={{}} >
            {loading && loadingPanel}
            <LocalizationProvider language={contextState.localeId}>
                <IntlProvider locale={contextState.localeId}>
                    <AppContext.Provider value={{ ...contextState, onLanguageChange, changeTheme }}>
                        <link rel="stylesheet" href={theme}></link>
                        <NotificationGroup
                            style={{
                                right: 0,
                                bottom: 50,
                                alignItems: "flex-start",
                                flexWrap: "wrap-reverse",
                            }}
                        >
                            <Fade enter={true} exit={true}>
                                {toggleShowErrorMessage && (
                                    <Notification
                                        type={{ style: "error", icon: true }}
                                        closable={true}
                                        onClose={() => setToggleShowErrorMessage(false)}
                                    >
                                        <span>{errorMessage}</span>
                                    </Notification>
                                )}
                            </Fade>
                        </NotificationGroup>
                        {isAuthenticated && (
                            <NotificationGroup
                                style={{
                                    right: 10,
                                    bottom: 10,
                                    alignItems: 'flex-start',
                                    flexWrap: 'wrap',
                                    zIndex: 10000,                                    
                                }}
                            >
                                <Fade enter={true} exit={true}>
                                    {mqttNotifications.map((notification) => (
                                        <Notification
                                            key={notification.id}
                                            style={{ fontSize: 'small', background: '#F9E1D5' }}
                                            type={{ style: notification.style, icon: true }}
                                            closable={true}
                                            onClose={() => {
                                                setMqttNotifications((prev) => prev.filter((item) => item.id !== notification.id));
                                            }}
                                        >
                                            {notification.message}
                                        </Notification>
                                    ))}
                                </Fade>
                            </NotificationGroup>
                        )}
                        <Routes>
                            <Route path="/new-password" element={<NewPassword />} />
                            {isAuthenticated ?
                                <>
                                    <Route path="/" element={<LoginPage handleLogin={handleLogin} />} />                                    
                                    <Route path="/home" element={<DrawerContainer theme={theme} />} >
                                        <Route
                                            path="/home/dashboard"
                                            element={
                                                <Dashboard
                                                    //ref={childRef} 
                                                    //batteryPercentage={batteryPercentage} 
                                                    //regularTag={regularTag}
                                                />
                                            }
                                        />
                                        <Route path="/home/synoptic" element={<SynopticDevices />} />
                                        <Route path="/home/beacons" element={<Beacons />} />
                                        <Route path="/home/areas" element={<Areas />} />
                                        <Route path="/home/map" element={<Maps />} />
                                        <Route path="/home/alarms" element={
                                            <Alarms
                                                handlePopupInfo={handlePopupInfo}
                                                setShowPopupInfo={setShowPopupInfo}
                                                showPopupInfo={showPopupInfo}
                                                handlePopupAlarm={handlePopupAlarm}
                                                setShowPopupAlarm={setShowPopupAlarm}
                                                showPopupAlarm={showPopupAlarm}
                                            />
                                        }
                                        />
                                        <Route
                                            path="/home/settings"
                                            element={
                                                <Settings
                                                    changeTheme={changeTheme}
                                                    batteryPercentage={(val: any) => updateBatteryPercentage(val)}
                                                    valueRefreshAPI={(val: any) => updateValueRefreshAPI(val)}
                                                    regularTag={(val: any) => updateRegularTag(val)}
                                                    statusDevice={(val: any) => updateStatusDevice(val)}
                                                />
                                            }
                                        />
                                        <Route path="/home/configuration/maps" element={<ConfigMaps />} />
                                        <Route path="/home/configuration/beacons" element={<ConfigBeacons />} />
                                        <Route path="/home/configuration/devices/devices-enable" element={<ConfigDevicesEnable />} />
                                        <Route path="/home/configuration/devices/devices-management" element={<ConfigDevicesManagement />} />
                                        <Route path="/home/configuration/devices/devices-binding" element={<ConfigDevicesBinding />} />
                                        <Route path="/home/configuration/rules/timeslot" element={<ConfigRulesTimeslot />} />
                                        <Route path="/home/configuration/rules/subscription" element={<ConfigRulesSubscription />} />
                                        <Route path="/home/configuration/rules/alarm-action-handlers" element={<ConfigRulesAlarmActionHandlers />} />
                                        <Route path="/home/configuration/rules/subscription-api" element={<ConfigRulesSubscriptionAPI />} />
                                        <Route path="/home/configuration/locator/collector" element={<ConfigLocatorCollector />} />
                                        <Route path="/home/configuration/locator/server" element={<ConfigLocatorServer />} />
                                        <Route path="/home/configuration/privacy" element={<ConfigPrivacy />} />
                                        <Route path="/home/configuration/registry/groups" element={<RegistryGroups />} />
                                        <Route path="/home/configuration/registry/jobs" element={<RegistryJobs />} />
                                        <Route path="/home/configuration/registry/accounts" element={<RegistryAccounts />} />
                                        <Route path="/home/configuration/analysis/heatmap" element={<HeatMap />} />
                                        <Route path="/home/configuration/analysis/path_history" element={<SpaghettiChart />} />
                                        <Route path="/home/configuration/analysis/report" element={<Report />} />
                                        <Route path="/home/configuration/maps/zones" element={<ConfigZones />} />
                                        <Route path="/home/configuration/maps/maps" element={<ConfigMaps />} />
                                    </Route>
                                </>
                                :
                                <>
                                    <Route path="*" element={<LoginPage handleLogin={handleLogin} />} />
                                </>
                            }
                        </Routes>
                    </AppContext.Provider>
                </IntlProvider>
            </LocalizationProvider>
        </div>
    );
}

export default App;
